import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { authApi } from '../../services/api';

const CompanyProfilePage = () => {
  const { user, getCompanyProfile, updateCompanyProfile } = useAuth();
  const { companies } = useData();
  const [company, setCompany] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    username: '',
    password: '',
    location: ''
  });

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        setLoading(true);
        // First try to get profile from API
        const profile = await getCompanyProfile();
        
        if (profile) {
          console.log('Company profile from API:', profile);
          setCompany(profile);
          setFormData({
            name: profile.name || '',
            email: profile.email || '',
            mobile: profile.mobile || profile.phone || '',
            username: profile.username || '',
            password: '',
            location: profile.location || profile.address || ''
          });
        } else {
          // Fallback to finding company in context
          const companyId = user?.companyId;
          const foundCompany = companies.find(c => c.id === companyId);
          
          if (foundCompany) {
            console.log('Company from context:', foundCompany);
            setCompany(foundCompany);
            setFormData({
              name: foundCompany.name || '',
              email: foundCompany.email || '',
              mobile: foundCompany.mobile || foundCompany.phone || '',
              username: foundCompany.username || '',
              password: '',
              location: foundCompany.location || foundCompany.address || ''
            });
          }
        }
      } catch (error) {
        console.error('Error fetching company profile:', error);
        setError('Failed to load company profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyProfile();
  }, [user, companies, getCompanyProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedProfile = await updateCompanyProfile(formData);
      if (updatedProfile) {
        setCompany(updatedProfile);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating company profile:', error);
      setError('Failed to update profile. Please try again.');
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading company profile...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!company) {
    return <div style={styles.error}>Company profile not found.</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Company Profile</h1>
      </div>

      <div style={styles.profileContainer}>
        {isEditing ? (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label htmlFor="name" style={styles.label}>Company Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="mobile" style={styles.label}>Mobile</label>
              <input
                type="text"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="username" style={styles.label}>Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="password" style={styles.label}>Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                style={styles.input}
                placeholder="Leave blank to keep current password"
              />
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="location" style={styles.label}>Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button type="submit" style={styles.saveButton}>
                Save Changes
              </button>
            </div>
          </form>
        ) : (
          <div style={styles.profileDetails}>
            <div style={styles.profileHeader}>
              <h2 style={styles.companyName}>{company.name}</h2>
              <button
                onClick={() => setIsEditing(true)}
                style={styles.editButton}
              >
                Edit Profile
              </button>
            </div>

            <div style={styles.infoSection}>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Email:</span>
                <span style={styles.infoValue}>{company.email}</span>
              </div>

              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Mobile:</span>
                <span style={styles.infoValue}>{company.mobile || company.phone}</span>
              </div>

              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Username:</span>
                <span style={styles.infoValue}>{company.username || company.name || 'Not set'}</span>
              </div>

              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Location:</span>
                <span style={styles.infoValue}>{company.location || company.address || 'Not set'}</span>
              </div>

              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Join Date:</span>
                <span style={styles.infoValue}>
                  {company && company.join_date ? 
                    new Date(company.join_date).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1rem',
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    color: 'var(--text-color)',
    marginBottom: '1rem',
  },
  profileContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: 'var(--card-shadow)',
  },
  profileHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid var(--border-color)',
  },
  companyName: {
    margin: 0,
    color: 'var(--text-color)',
    fontSize: '1.5rem',
  },
  editButton: {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  infoSection: {
    marginBottom: '1.5rem',
  },
  infoItem: {
    display: 'flex',
    marginBottom: '0.75rem',
  },
  infoLabel: {
    width: '150px',
    fontWeight: 'bold',
    color: 'var(--text-light)',
  },
  infoValue: {
    flex: 1,
    color: 'var(--text-color)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: 'var(--text-color)',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    fontSize: '1rem',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '1rem',
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'var(--danger-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  saveButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'var(--success-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    fontSize: '1rem',
  },
  error: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--danger-color)',
    fontSize: '1rem',
  },
};

export default CompanyProfilePage;
